import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { useUser } from "../context/UserContext";

export default function MfaSetupPage() {
  const navigate = useNavigate();
  const { login, user } = useUser();
  const [status, setStatus] = useState({ enabled: false, pendingSetup: false });
  const [loading, setLoading] = useState(true);
  const [currentPassword, setCurrentPassword] = useState("");
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    async function loadStatus() {
      try {
        const { data } = await api.get("/auth/mfa/status");
        setStatus(data || {});
      } catch (requestError) {
        setError(requestError?.response?.data?.message || "MFA-status ophalen mislukt.");
      } finally {
        setLoading(false);
      }
    }

    loadStatus();
  }, []);

  async function startSetup() {
    if (!currentPassword) {
      setError("Vul je huidige wachtwoord in.");
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      const { data } = await api.post("/auth/mfa/setup/start", { currentPassword });
      setSetup(data);
      setCode("");
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "MFA-setup starten mislukt.");
    } finally {
      setSubmitting(false);
    }
  }

  async function verifySetup() {
    if (!code.trim()) {
      setError("Vul de code uit je authenticator-app in.");
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      const { data } = await api.post("/auth/mfa/setup/verify", { code: code.trim() });
      if (!data?.accessToken || !data?.refreshToken) {
        throw new Error("MFA is ingesteld, maar er is geen vernieuwde sessie ontvangen.");
      }
      login(data.accessToken, data.refreshToken);
      setBackupCodes(data?.backupCodes || []);
      setStatus((previous) => ({ ...previous, enabled: true, pendingSetup: false }));
      setSetup(null);
      setCurrentPassword("");
      setCode("");
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "MFA verifiëren mislukt.");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return <div className="min-h-screen grid place-items-center bg-slate-100">MFA-status laden...</div>;
  }

  return (
    <main className="min-h-screen bg-slate-100 px-4 py-10">
      <section className="mx-auto max-w-xl rounded-xl bg-white p-6 shadow">
        <h1 className="text-2xl font-bold text-slate-900">Tweestapsverificatie instellen</h1>
        <p className="mt-2 text-slate-700">Rond deze stap af voordat je verdergaat in Logboek24.</p>

        {error && <p className="mt-4 rounded bg-red-50 p-3 text-sm text-red-700">{error}</p>}

        {status.enabled ? (
          <div className="mt-5 space-y-4">
            <p className="rounded bg-green-50 p-3 text-green-800">MFA is ingeschakeld.</p>
            {backupCodes.length > 0 && (
              <div className="rounded border border-amber-300 bg-amber-50 p-4">
                <p className="font-semibold text-amber-900">Bewaar deze backupcodes veilig. Ze worden maar één keer getoond.</p>
                <ul className="mt-3 space-y-1 font-mono text-sm text-amber-950">
                  {backupCodes.map((backupCode) => <li key={backupCode}>{backupCode}</li>)}
                </ul>
              </div>
            )}
            <button type="button" onClick={() => navigate(resolvePostMfaPath(user), { replace: true })} className="rounded bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700">Verder naar Logboek24</button>
          </div>
        ) : (
          <div className="mt-5 space-y-4">
            {!setup ? (
              <>
                <label className="block text-sm font-medium text-slate-800" htmlFor="mfa-current-password">Huidig wachtwoord</label>
                <input id="mfa-current-password" type="password" autoComplete="current-password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} className="w-full rounded border border-slate-300 px-3 py-2" />
                <button type="button" onClick={startSetup} disabled={submitting} className="rounded bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700 disabled:opacity-60">{submitting ? "Setup starten..." : "Start MFA-setup"}</button>
              </>
            ) : (
              <>
                <p className="text-slate-700">Scan de QR-code met Microsoft Authenticator en voer daarna de zescijferige code in.</p>
                {setup.qrCodeDataUrl && <img src={setup.qrCodeDataUrl} alt="MFA QR-code" className="h-48 w-48 border bg-white p-1" />}
                <p className="break-all text-sm text-slate-700">Handmatige sleutel: <span className="font-mono">{setup.secret || setup.secretMasked}</span></p>
                <label className="block text-sm font-medium text-slate-800" htmlFor="mfa-code">Authenticator-code</label>
                <input id="mfa-code" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(event) => setCode(event.target.value)} className="w-full rounded border border-slate-300 px-3 py-2" />
                <button type="button" onClick={verifySetup} disabled={submitting} className="rounded bg-green-600 px-4 py-2 font-semibold text-white hover:bg-green-700 disabled:opacity-60">{submitting ? "Verifiëren..." : "Bevestig en activeer MFA"}</button>
              </>
            )}
          </div>
        )}
      </section>
    </main>
  );
}

function resolvePostMfaPath(user) {
  const roleNames = [
    ...(Array.isArray(user?.roles) ? user.roles : []),
    user?.rol,
    user?.role,
  ].map((item) => String(typeof item === "object" ? item?.name || item?.naam || "" : item || "").trim().toLowerCase());
  if (roleNames.includes("monteur")) return "/monteur/werkbonnen";
  if (roleNames.includes("platformadmin")) return "/admin/dashboard";
  if (roleNames.some((role) => ["projectleider", "werkvoorbereider", "tenantadmin", "controller", "zzp_owner"].includes(role))) return "/pl";
  return "/dashboard";
}
